import type {
  ChangementItem,
  BrouillonItem,
  Item,
  ReponseBrief,
  ReponseClePush,
  ReponseConnexion,
  ReponseItems,
  ReponseAssistant,
  ReponseMembres,
  TourAssistant,
} from '@camellie/shared';

export class ErreurApi extends Error {
  constructor(
    readonly statut: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

/** Declenche quand le serveur repond 401 : l'app retourne a la connexion. */
type EcouteurDeconnexion = () => void;
const ecouteurs = new Set<EcouteurDeconnexion>();

export function surDeconnexion(f: EcouteurDeconnexion): () => void {
  ecouteurs.add(f);
  return () => ecouteurs.delete(f);
}

async function appel<T>(chemin: string, init: RequestInit = {}): Promise<T> {
  let reponse: Response;
  try {
    reponse = await fetch(chemin, {
      ...init,
      // Indispensable : le cookie de session est httpOnly, c'est le navigateur
      // qui doit le joindre, on ne peut pas le lire depuis le JS.
      credentials: 'include',
      headers: {
        ...(init.body ? { 'content-type': 'application/json' } : {}),
        ...init.headers,
      },
    });
  } catch (e) {
    // Seul cas ou le serveur est vraiment injoignable : fetch lui-meme a
    // echoue (pas de reseau, DNS, TLS). Tout le reste est une reponse, et
    // merite un message qui dit laquelle.
    throw new ErreurApi(0, 'reseau', 'Pas de reseau : ' + (e instanceof Error ? e.message : String(e)));
  }

  if (reponse.status === 401) {
    for (const f of ecouteurs) f();
    throw new ErreurApi(401, 'non_authentifie', 'Session expiree.');
  }

  if (reponse.status === 204) return undefined as T;

  // On lit le texte plutot que d'appeler .json() directement : quand une
  // protection du reseau ou un proxy intercale une page HTML, .json() echoue
  // sans rien dire de ce qui a ete recu. Ici on peut au moins rapporter le
  // statut et le type de contenu.
  const brut = await reponse.text().catch(() => '');
  let corps: (T & { erreur?: string; message?: string }) | null = null;
  try {
    corps = brut ? (JSON.parse(brut) as T & { erreur?: string; message?: string }) : null;
  } catch {
    corps = null;
  }

  if (!reponse.ok) {
    const type = reponse.headers.get('content-type') ?? 'type inconnu';
    throw new ErreurApi(
      reponse.status,
      corps?.erreur ?? 'http_' + String(reponse.status),
      corps?.message ?? 'Reponse ' + String(reponse.status) + ' (' + type + ')',
    );
  }

  if (corps === null) {
    const type = reponse.headers.get('content-type') ?? 'type inconnu';
    throw new ErreurApi(
      reponse.status,
      'reponse_illisible',
      'Reponse ' + String(reponse.status) + ' non-JSON (' + type + ')',
    );
  }

  return corps as T;
}

export const api = {
  membres: () => appel<ReponseMembres>('/api/membres'),

  connexion: (membre_id: string, pin: string) =>
    appel<ReponseConnexion>('/api/auth/pin', {
      method: 'POST',
      body: JSON.stringify({ membre_id, pin }),
    }),

  moi: () => appel<ReponseConnexion>('/api/auth/moi'),

  deconnexion: () => appel<{ ok: boolean }>('/api/auth/deconnexion', { method: 'POST' }),

  items: () => appel<ReponseItems>('/api/items'),

  brief: () => appel<ReponseBrief>('/api/brief/aujourdhui'),

  creerItem: (brouillon: BrouillonItem) =>
    appel<{ item: Item }>('/api/items', { method: 'POST', body: JSON.stringify(brouillon) }),

  majItem: (id: string, champs: ChangementItem) =>
    appel<{ item: Item }>('/api/items/' + encodeURIComponent(id), {
      method: 'PATCH',
      body: JSON.stringify(champs),
    }),

  clePush: () => appel<ReponseClePush>('/api/push/cle'),

  abonner: (endpoint: string, cles: { p256dh: string; auth: string }) =>
    appel<{ ok: boolean }>('/api/push/abonner', {
      method: 'POST',
      body: JSON.stringify({ endpoint, cles }),
    }),

  assistant: (texte: string, historique: TourAssistant[] = []) =>
    appel<ReponseAssistant>('/api/assistant', {
      method: 'POST',
      body: JSON.stringify({ texte, historique }),
    }),

  testerPush: () => appel<{ apercu: { titre: string; corps: string } }>('/api/push/test', { method: 'POST' }),
};
